import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';

import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';

import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * =========================
   * QUANTITY CHANGE HANDLER
   * =========================
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = lineItemRow?.querySelector('text-component');
    textComponent?.shimmer();
  }

  /**
   * =========================
   * OPTIMISTIC REMOVE (INSTANT UI)
   * =========================
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];

    // INSTANT UI REMOVAL (no waiting for fetch)
    if (row) {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
      } else {
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * =========================
   * MAIN UPDATE LOGIC (FAST MODE)
   * =========================
   */
  updateQuantity(config) {
    const { line, quantity, action } = config;

    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    this.refs.cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((r) => r.text())
      .then((text) => {
        const parsed = JSON.parse(text);

        resetShimmer(this);

        if (parsed.errors) {
          this.#handleCartError(line, parsed);
          return;
        }

        const newHtml = new DOMParser().parseFromString(
          parsed.sections[this.sectionId],
          'text/html'
        );

        const newItemCount =
          newHtml.querySelector('[ref="cartItemCount"]')?.textContent;

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            itemCount: newItemCount ? parseInt(newItemCount, 10) : 0,
            source: 'cart-items-component',
            sections: parsed.sections,
          })
        );

        // ⚡ INSTANT PATCH MODE (NO FULL MORPH)
        this.#patchChangedRows(newHtml);

        this.#updateCartTotal(newHtml);
        this.#syncSingleLineQuantity(parsed, line);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * =========================
   * INSTANT DOM PATCH ENGINE
   * =========================
   */
  #patchChangedRows(newDoc) {
    const newRows = newDoc.querySelectorAll('[data-key]');
    const oldRows = this.querySelectorAll('[data-key]');

    const oldMap = new Map();
    oldRows.forEach((row) => oldMap.set(row.dataset.key, row));

    newRows.forEach((newRow) => {
      const key = newRow.dataset.key;
      const existing = oldMap.get(key);

      if (existing) {
        existing.replaceWith(newRow);
        oldMap.delete(key);
      }
    });

    // remove deleted rows
    for (const [, row] of oldMap) {
      row.remove();
    }
  }

  /**
   * =========================
   * SINGLE LINE SYNC (FAST)
   * =========================
   */
  #syncSingleLineQuantity(cart, line) {
    const item = cart.items?.[line - 1];
    if (!item) return;

    const selector = document.querySelector(
      `quantity-selector-component[data-variant-id="${item.variant_id}"]`
    );

    const input = selector?.querySelector('input[data-cart-quantity]');
    if (input) {
      input.setAttribute('data-cart-quantity', item.quantity);
    }
  }

  /**
   * =========================
   * TOTAL UPDATE ONLY
   * =========================
   */
  #updateCartTotal(newDoc) {
    const newTotal = newDoc.querySelector('[ref="cartTotal"]');
    const oldTotal = this.querySelector('[ref="cartTotal"]');

    if (newTotal && oldTotal) {
      oldTotal.replaceWith(newTotal);
    }
  }

  /**
   * =========================
   * CART UPDATE EVENT
   * =========================
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const html = event.detail.data.sections?.[this.sectionId];
    if (!html) return;

    const newDoc = new DOMParser().parseFromString(html, 'text/html');

    this.#patchChangedRows(newDoc);
    this.#updateCartTotal(newDoc);
  };

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * =========================
   * ERROR HANDLING
   * =========================
   */
  #handleCartError(line, parsed) {
    const selector = this.refs.quantitySelectors[line - 1];
    const input = selector?.querySelector('input');

    if (!input) return;

    input.value = input.defaultValue;

    const error = this.refs[`cartItemError-${line}`];
    const container = this.refs[`cartItemErrorContainer-${line}`];

    if (error && container) {
      error.textContent = parsed.errors;
      container.classList.remove('hidden');
    }
  }

  /**
   * =========================
   * UI STATE HELPERS
   * =========================
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      'cart-quantity-selector-component'
    )) {
      selector.updateButtonStates?.();
    }
  }

  /**
   * =========================
   * GETTERS
   * =========================
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}